// Copyright (c) Docugami, Inc. All rights reserved.

import { Icon, ISpinnerStyles, Spinner, Toggle, TooltipHost } from "@fluentui/react";
import { DgButton } from "components/common/DgCommonStyledComponents";
import { dgFontFamily, userFontFamily } from "components/common/Fonts";
import { OverflowTextContainer } from "components/doc_hosting/taskpane/presentation/TaskPaneStyles";
import { ExplorerTextButton } from "components/myContentExplorer/ExplorerStyles";
import { appContentColor, primaryTheme } from "components/shell/presentation/AppStyles";
import { useTimeout } from "hooks/Timeout";
import { OrganizationSignalRConnectionInstance } from "networking/OrganizationSignalRConnection";
import { ExportConfiguration, ExportConfigurationStatus, WorkPathType } from "networking/WorkPathApi";
import { useEffect, useMemo, useState } from "react";
import { getIconDescriptorForStorageProvider, getProviderFriendlyName } from "storage/StorageProviderHelpers";
import { useIsEvaluatingConfigurationQuery } from "store/work_path/workPathSlice";
import styled from "styled-components";
import { IconRenderer } from "./IconRenderer";
import { ProviderDestination } from "./ProviderDestination";
import { getIconInfoForWorkPath } from "./WorkPathsHelper";

const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(150px, 250px) 450px minmax(150px, 250px);
    grid-template-rows: auto 20px auto;
    .lowerCenter {
        grid-row: 2;
        grid-column: 2;
        grid-row-end: span 2;
    }
`;

const ContentGridText = styled.span<{color?: string}>`
    font-family: ${dgFontFamily};
    font-size: 30px;
    font-weight: 600;
    line-height: 37px;
    color: ${props => !!props.color ? props.color : appContentColor};
`;

const ContentLabel = styled.span`
    font-family: ${userFontFamily};
    font-size: 18px;
    font-weight: 400;
    line-height: 22px;
    text-overflow: ellipsis;
    color: ${primaryTheme.palette.black};
`;

const SettingsLabelText = styled(ContentLabel)`
    color: ${appContentColor};
    font-family: ${dgFontFamily};
    font-weight: 600;
`

const GridItem = styled.div<{ col?: number, row?: number }>`
    ${props => props.col !== undefined && `grid-column: ${props.col};`}
    ${props => props.row !== undefined && `grid-row: ${props.row};`}
`

const ContentGridItem = styled(GridItem)`
    display: flex;
    margin: auto;
    justify-content: center;
    text-align: center;
    ${props => props.col !== undefined && `grid-column: ${props.col};`}
    ${props => props.row !== undefined && `grid-row: ${props.row};`}
`

const SettingsGrid = styled.div<{ disabled?: boolean }>`
    display: grid;
    grid-template-columns: auto 400px;
    grid-template-rows: auto 30px auto;
    margin: auto;
    margin-top: 75px;
    opacity: ${props => !!props.disabled ? .3 : 1};
    .label {
        text-align: right;
    }
`

const SettingsContent = styled(GridItem)`
    display: flex;
    align-items: center;
    margin-left: 10px;
`

const CenteredContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin: auto;
`

const ConfigurationContent = styled(CenteredContentContainer)`
    margin: auto;
    padding: 50px 0px;
`

const Footer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;
    gap: 20px;
`

const DeleteButton = styled(ExplorerTextButton)`
    display: flex;
    gap: 5px;
    :hover {
        .noDecoration {
            text-decoration: none;
        }
    }
`;

export interface ConfigurationViewProps {
    workPathType: WorkPathType;
    configuration: ExportConfiguration;
    workPathId: string;
    title: string;
    onClose: () => void;
    onDelete: () => void;
    onToggleEnabled: () => void;
    updatingStatus?: boolean;
}

export const ExportConfigurationView = (props: ConfigurationViewProps): JSX.Element => {
    const [isEvaluating, setIsEvaluating] = useState(false);
    const [pendingEvalCompletion, setPendingEvalCompletion] = useState<boolean>(false);

    const { isLoading, isError, data } = useIsEvaluatingConfigurationQuery({ workPathId: props.workPathId, configurationId: props.configuration.id });

    useTimeout(() => setIsEvaluating(false), pendingEvalCompletion ? 2000 : undefined);

    useEffect(() => {
        if (data !== undefined) {
            setIsEvaluating(data);
        }
        else if (isError) {
            setIsEvaluating(false);
        }
    }, [data, isError])

    useEffect(() => {
        // Hook up the SignalR notifications
        if (!!OrganizationSignalRConnectionInstance) {
            const onEvaluationStarted = () => {
                setIsEvaluating(true);
                setPendingEvalCompletion(false);
            }

            const onEvaluationEnded = () => setPendingEvalCompletion(true);

            OrganizationSignalRConnectionInstance.connection.on("workPathExportEvaluationStarted", onEvaluationStarted);
            OrganizationSignalRConnectionInstance.connection.on("workPathExportEvaluationEnded", onEvaluationEnded);

            return () => {
                OrganizationSignalRConnectionInstance?.connection.off("workPathExportEvaluationStarted", onEvaluationStarted);
                OrganizationSignalRConnectionInstance?.connection.off("workPathExportEvaluationEnded", onEvaluationEnded);
            }
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.workPathId, props.configuration.id]);

    const [syncText, syncTextColor] = useMemo((): [string, string] => {
        if (isEvaluating) {
            return ["Syncing to", appContentColor];
        }
        else if (props.configuration.status === ExportConfigurationStatus.Error) {
            return ["Sync paused", primaryTheme.palette.red];
        }
        else {
            const text = props.configuration.isDisabled ? "Connected To" : "Synced To";
            return [text, appContentColor];
        }
    }, [props.configuration.status, props.configuration.isDisabled, isEvaluating]);

    function getStatusElement(): JSX.Element | null {
        if (isEvaluating || isLoading) {
            // Sync is actively running - show a spinner
            return <Spinner styles={spinnerStyle} className="lowerCenter" />
        }

        return null;
    }

    const spinnerStyle: Partial<ISpinnerStyles> = {
        circle: {
            width: "40px",
            height: "40px",
            borderWidth: "4px"
        }
    }

    const isExportEnabled = !!props.updatingStatus ? !!props.configuration.isDisabled : !props.configuration!.isDisabled;
    return (
        <>
            <ConfigurationContent>
                <ContentGrid>
                    <ContentGridItem>
                        <IconRenderer source={getIconInfoForWorkPath(props.workPathType)} height={130} width={150} />
                    </ContentGridItem>
                    <ContentGridItem>
                        <ContentGridText color={syncTextColor}>
                            {syncText.toUpperCase()}
                        </ContentGridText>
                    </ContentGridItem>
                    <ContentGridItem>
                        <IconRenderer source={getIconDescriptorForStorageProvider(props.configuration.providerId!)} height={120} width={150} />
                    </ContentGridItem>
                    { getStatusElement() }
                    <ContentGridItem row={3}>
                        <ContentLabel>
                            <OverflowTextContainer maxLines={2}>
                                <TooltipHost content={props.title}>
                                    {props.title.toUpperCase()}
                                </TooltipHost>
                            </OverflowTextContainer>
                        </ContentLabel>
                    </ContentGridItem>
                    <ContentGridItem row={3} col={3}>
                        <ContentLabel>
                            {getProviderFriendlyName(props.configuration.providerId).toUpperCase()}
                        </ContentLabel>
                    </ContentGridItem>
                </ContentGrid>
                <SettingsGrid>
                    <SettingsLabelText className="label">Destination:</SettingsLabelText>
                    <SettingsContent>
                        <ProviderDestination
                            providerId={props.configuration.providerId}
                            destinationPath={props.configuration.destinationPath}
                            readonly />
                    </SettingsContent>
                    <GridItem row={3}>
                        <SettingsLabelText className="label">Export Enabled:</SettingsLabelText>
                    </GridItem>
                    <SettingsContent row={3}>
                        <Toggle
                            disabled={!!props.updatingStatus}
                            checked={isExportEnabled}
                            onChange={() => props.onToggleEnabled()}
                            styles={{ root: { margin: 0 } }} />
                    </SettingsContent>
                </SettingsGrid>
            </ConfigurationContent>
            <Footer>
                <DeleteButton onClick={props.onDelete}>
                    <span className="noDecoration">
                        <Icon iconName="Delete" />
                    </span>
                    Remove Configuration
                </DeleteButton>
                <div style={{flex: 1}} />
                <DgButton onClick={props.onClose}>Close</DgButton>
            </Footer>
        </>
    )
}
